/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import Accordion from '@ion/dls/src/components/accordion/ExpansionPanel';
import {CTALink} from '@ion/dls/src/components/buttons/CTALink';
import Chip from '@ion/dls/src/components/chip/Chip';
import Slider from '@ion/dls/src/components/slider/Slider';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {TRAFFIC_SUB_LAYER_INCIDENT, TRAFFIC_SUB_LAYER_LIVE_TRAFFIC} from '../constants';
import {getLayerIndex} from '../helpers/MapViewUtils';
import {
  LayerSettingsHeader,
  LayerSettingsMarks as Marks,
  LayerSettingsProps,
  LayerSettingsStyles,
  LayerSettingsSubLayersProps
} from './LayerSettings';
import styles from './LayerChooser.module.scss';

// defines the prop definitions for the TrafficLayerSettings
export type TrafficLayerSettingsProps = LayerSettingsProps & {
  mapImageLayer: __esri.MapImageLayer;
  onOpacityChange: Function,
  onToggleExpand?: Function,
  setTrafficSubLayers?: Function,
  opacity: number,
  trafficSubLayers?: string[],
  url: string;
  expandedLayer: string
};

// defines the styles used by the TrafficLayerSettings
const useStyles = makeStyles(theme => (LayerSettingsStyles()));

// The following lists the ids for the Live Traffic layer in the traffic service
export const LiveTraffic = [{
  id      : 6, // North America
  visible : true
},
{
  id      : 14, // South America
  visible : true
},
{
  id      : 22, // Europe
  visible : true
},
{
  id      : 30, // Middle East and Africa
  visible : true
},
{
  id      : 42, // Asia Pacific
  visible : true
}];

// The following lists the ids for the Incidents layer in the traffic service:
export const Incidents = [{
  id      : 2, // North America
  visible : true
},
{
  id      : 3, // Traffic Incidents Intermediate
  visible : true
},
{
  id      : 4, // Traffic Incidents Detailed
  visible : true
},
{
  id      : 10, // South America
  visible : true
},
{
  id      : 11, // Traffic Incidents Intermediate
  visible : true
},
{
  id      : 12, // Traffic Incidents Detailed
  visible : true
},
{
  id      : 18, // Europe
  visible : true
},
{
  id      : 19, // Traffic Incidents Intermediate
  visible : true
},
{
  id      : 20, // Traffic Incidents Detailed
  visible : true
},
{
  id      : 26, // Middle East and Africa
  visible : true
},
{
  id      : 27, // Traffic Incidents Intermediate
  visible : true
},
{
  id      : 28, // Traffic Incidents Detailed
  visible : true
},
{
  id      : 46, // Asia Pacific
  visible : true
},
{
  id      : 47, // Traffic Incidents Intermediate
  visible : true
},
{
  id      : 48, // Traffic Incidents Detailed
  visible : true
}];

/**
 * @SubLayers
 *
 * Defines the SubLayers Tab / Section of the TrafficLayerSettings
 */
const SubLayers = ({
  opacity = 1, changeOpacity, subLayersChip,
  updateSubLayersChip
}: LayerSettingsSubLayersProps) => {
  const [value, setValue] = useState(opacity);
  const labels = translate('maps:traffic', {returnObjects: true});

  useEffect(() => {
    setValue(opacity);
  }, [opacity]);

  useEffect(() => {
    changeOpacity(value);
  }, [value]);

  const handleChipClick = (e, label) => {
    const nextState = subLayersChip.map((chip) => {
      if (chip.label === label) {
        chip.isSelected = !chip.isSelected;
      }
      return chip;
    });
    updateSubLayersChip(nextState);
  };

  const handleChange = (event, value) => {
    setValue(value);
  };

  return (
    <div className='subLayers'>
      <div className={styles.subLayersChip}>
        {subLayersChip.map((chip, index) => (
          <div key={index}>
            <Chip label={chip.label}
              variant='outlined'
              isSelected={chip.isSelected}
              onClick={function(e) { handleChipClick(e, chip.label); }}
              onDelete={function(e) { handleChipClick(e, chip.label); }} />
          </div>
        ))}
      </div>
      <div className={styles.subLayersSlider}>
        <span className={styles.subLayersHeader}>{translate('maps:traffic.opacity')}</span>
        <Slider step={0.1}
          marks={Marks}
          value={value}
          max={1}
          onSlide={function(e, val) { handleChange(e, val); }} />
      </div>
    </div>
  );
};

/**
 * @Legend
 *
 * Defines the Legend Tab / Section of the TrafficLayerSettings
 */
const Legend = () => {
  const classes: any                          = useStyles();
  const TrafficRender                         = [];
  const {stopAndGo, slow, moderate, freeFlow} = dlsAlt('maps', 'legend', 'traffic');
  const labels                                = translate('maps:traffic', {returnObjects: true});
  const traffic                               = [
    {color: stopAndGo, title: labels.stopAndGo},
    {color: slow, title: labels.slow},
    {color: moderate, title: labels.moderate},
    {color: freeFlow, title: labels.freeFlow}
  ];
  traffic.map(({title, color}) => {
    TrafficRender.push(
      <div className={classes.legendIcon} key={title}>
        <div style={{backgroundColor: `${color}`}} className={classes.rectangle} />
        <label className={classes.legendLabel}>{`${title}`}</label>
      </div>
    );
  });

  return (
    <div className={classes.trafficContainer}>
      <div className={classes.trafficItemLeft}>
        <CTALink className={classes.subLabel}>{labels.incidents}</CTALink>
        <div style={{marginLeft: '3%', marginBottom: '10px', textAlign: 'center'}}>
          <div className={classes.legendIcon}>
            <div style={{backgroundColor: dlsColor('caRed')}} className={classes.circle} />
            <label className={classes.legendLabel}>{labels.critical}</label>
          </div>
          <div className={classes.legendIcon}>
            <div style={{backgroundColor: dlsColor('caRed'), borderColor: dlsColor('caYellow')}} className={classes.borderCircle1} />
            <label className={classes.legendLabel}>{labels.major}</label>
          </div>
          <div className={classes.legendIcon}>
            <div style={{backgroundColor: dlsColor('caRed'), borderColor: dlsColor('caYellow')}} className={classes.borderCircle2} />
            <label className={classes.legendLabel}>{labels.minor}</label>
          </div>
          <div className={classes.legendIcon}>
            <div style={{backgroundColor: dlsColor('caYellow')}} className={classes.circle} />
            <label className={classes.legendLabel}>{labels.lowImpact}</label>
          </div>
        </div>
      </div>
      <div>
        <CTALink className={classes.subLabel}>{labels.header}</CTALink>
        {TrafficRender}
      </div>
    </div>
  );
};

// defines the unique identifier for the TrafficLayerSettings
export const TRAFFIC_LAYER = 'traffic';

/**
 * @TrafficLayerSettings
 *
 * The Traffic Layer Settings provides a component which allows users to select
 * specific Traffic layer related attributes to be displayed within a DLSMap.
 *
 * The TrafficLayerSettings component represents the "Traffic Layers" DLS feature:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/01_DLS_Components?node-id=1904%3A13962
 *
 * ```typescript
 *
 *   <TrafficLayerSettings url={}
 *     isEnabled={}
 *     onToggle={}
 *     onOpacityChange={}
 *     opacity={}
 *     map={} />
 *
 * ```
 */
export const TrafficLayerSettings = ({
  map,
  url,
  layers,
  isEnabled,
  mapImageLayer,
  onOpacityChange,
  setTrafficSubLayers,
  opacity,
  trafficSubLayers,
  onToggle,
  onToggleExpand,
  expandedLayer
}: TrafficLayerSettingsProps) => {
  const classes: any                = useStyles();
  const [activeTab, setActiveTab]   = useState('sublayers');
  const [defaultLayer, setSelected] = useState(isEnabled);
  const layerRef                    = useRef();
  const labels                      = translate('maps:traffic', {returnObjects: true});
  const MapImageLayer               = mapImageLayer;
  const [subLayersChip, updateSubLayersChip] = useState([
    {
      label      : labels.incident,
      name       : TRAFFIC_SUB_LAYER_INCIDENT,
      isSelected : false
    },
    {
      label      : labels.liveTraffic,
      name       : TRAFFIC_SUB_LAYER_LIVE_TRAFFIC,
      isSelected : true
    }
  ]);

  useEffect(() => {
    setSelected(isEnabled);
  }, [isEnabled]);

  useEffect(() => {
    const savedLayer = subLayersChip.map((chip) => {
      if (trafficSubLayers?.length) {
        const isSavedLayer = trafficSubLayers?.find(subLayer => subLayer === chip.name);
        chip.isSelected = !!isSavedLayer;
      }
      return chip;
    });

    updateSubLayersChip(savedLayer);
  }, [trafficSubLayers]);

  useEffect(() => {
    const selectedLayerNames = subLayersChip?.filter(
      layer => layer.isSelected
    ).map(layer => layer.name);
    setTrafficSubLayers(selectedLayerNames);
  }, [subLayersChip]);

  const loadMapModules = function() {
    const sublayers = defineSubLayer();
    if (layerRef.current) {
      (layerRef.current as any).sublayers = [];
    }
    if (defaultLayer) {
      layerRef.current = new MapImageLayer({
        url,
        sublayers : sublayers,
        title     : TRAFFIC_LAYER,
        id        : TRAFFIC_LAYER
      });
      map.layers.add(layerRef.current);
      map.layers.reorder(layerRef.current, getLayerIndex(map, TRAFFIC_LAYER));
      changeOpacity(opacity);
    } else {
      map.layers.remove(layerRef.current);
    }
  };

  useEffect(() => {
    loadMapModules();
  }, [defaultLayer, subLayersChip]);

  const onToggleLayer = useCallback(({target:{checked}}) => {
    setSelected(checked);
    onToggle(TRAFFIC_LAYER, checked, labels.header);
  }, [layers]);

  const defineSubLayer = useCallback(() => {
    if (subLayersChip[0].isSelected && subLayersChip[1].isSelected && defaultLayer) {
      return Incidents.concat(LiveTraffic);
    } else if (subLayersChip[0].isSelected && defaultLayer) {
      return Incidents;
    } else if (subLayersChip[1].isSelected && defaultLayer) {
      return LiveTraffic;
    }
  }, [subLayersChip, defaultLayer, LiveTraffic, Incidents]);

  const changeOpacity = useCallback(newValue => {
    if (layerRef.current) {
      (layerRef.current as any).opacity = newValue;
      onOpacityChange(newValue);
    }
  }, []);

  const Details = (
    <>
      <div className={classes.layers}>
        <div className={classes.subLayer}>
          <CTALink className={activeTab === 'sublayers'
            ? classes.buttonSelected : classes.button} onClick={function() { setActiveTab('sublayers'); }}>
            {labels.subLayers}
          </CTALink>
        </div>
        <div>
          <CTALink className={activeTab === 'legend'
            ? classes.buttonSelected : classes.button} onClick={function() { setActiveTab('legend'); }}>
            {labels.legend}
          </CTALink>
        </div>
      </div>
      {activeTab === 'legend' && <Legend />}
      {activeTab === 'sublayers' && <SubLayers subLayersChip={subLayersChip} updateSubLayersChip={updateSubLayersChip}
        changeOpacity={changeOpacity} opacity={opacity} />}
    </>
  );

  return (
    <div className={classes.root}>
      <Grid container direction='row'>
        <Grid item xs={12} md={12}>
          <Paper className={classes.paper}>
            <Accordion children=''
              id={labels.header}
              expandedLayer={expandedLayer}
              onToggleExpand={onToggleExpand}
              header={<LayerSettingsHeader label={labels.header}
                isSelected={defaultLayer}
                onToggle={onToggleLayer} />} details={Details}
              detailsRootOverride={classes.accordionDetails} />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default TrafficLayerSettings;
